import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Seo from './Seo';
import Footer from './Footer';
import FaqSchema from './FaqSchema';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';
import MockInterviewPage from './MockInterviewPage';
import './MockInterviewLanding.css';

const HERO_IMG = '/images/mock-interview/hero-mock-stage.png';
const QUESTIONS_IMG = '/images/mock-interview/question-generator.png';
const ANSWER_IMG = '/images/mock-interview/model-answer.png';
const SCORE_IMG = '/images/mock-interview/performance-score.png';

/* ---------- Three.js stage canvas ---------- */
const StageCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 70;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    // 15 question-bubble points orbiting the center (1 per question)
    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.4, 'rgba(255,105,180,0.9)');
      g.addColorStop(0.75, 'rgba(147,112,219,0.3)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    const bubbleCount = 15;
    const positions = new Float32Array(bubbleCount * 3);
    const angles = new Float32Array(bubbleCount);
    const radii = new Float32Array(bubbleCount);
    for (let i = 0; i < bubbleCount; i++) {
      angles[i] = (i / bubbleCount) * Math.PI * 2;
      radii[i] = 24 + (i % 3) * 4;
      positions[i * 3] = Math.cos(angles[i]) * radii[i];
      positions[i * 3 + 1] = Math.sin(angles[i]) * radii[i] * 0.6;
      positions[i * 3 + 2] = (Math.random() - 0.5) * 18;
    }
    const bubbleGeo = new THREE.BufferGeometry();
    bubbleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const bubbleMat = new THREE.PointsMaterial({
      size: 4.5,
      map: sprite,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xffffff,
    });
    const bubbles = new THREE.Points(bubbleGeo, bubbleMat);
    scene.add(bubbles);

    // ambient particle field
    const fieldCount = 200;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 160;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 100;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 60;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.12;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 1.0,
      map: sprite,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      color: 0xffffff,
      opacity: 0.45,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    // central podium (icosahedron wireframe)
    const podiumGeo = new THREE.IcosahedronGeometry(7, 1);
    const podium = new THREE.Mesh(
      podiumGeo,
      new THREE.MeshBasicMaterial({ color: 0xff69b4, wireframe: true, transparent: true, opacity: 0.45 }),
    );
    scene.add(podium);

    let raf;
    let mx = 0;
    let my = 0;
    let t = 0;

    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);

    const onResize = () => {
      width = mount.clientWidth;
      height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.012;
      const bp = bubbleGeo.attributes.position.array;
      for (let i = 0; i < bubbleCount; i++) {
        const a = angles[i] + t * (0.4 + (i % 3) * 0.1);
        const r = radii[i] + Math.sin(t * 2 + i) * 1.4;
        bp[i * 3] = Math.cos(a) * r;
        bp[i * 3 + 1] = Math.sin(a) * r * 0.6;
        bp[i * 3 + 2] += 0.04 * Math.sin(t + i);
      }
      bubbleGeo.attributes.position.needsUpdate = true;

      const fp = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fp[i * 3 + 1] += fieldSpeeds[i] * 0.5;
        if (fp[i * 3 + 1] > 50) fp[i * 3 + 1] = -50;
      }
      fieldGeo.attributes.position.needsUpdate = true;

      podium.rotation.x += 0.005;
      podium.rotation.y += 0.007;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      bubbleGeo.dispose();
      bubbleMat.dispose();
      fieldGeo.dispose();
      fieldMat.dispose();
      podiumGeo.dispose();
      podium.material.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="mil-hero-canvas" aria-hidden="true" />;
};

/* ---------- Question generator demo (15 cards drop in) ---------- */
const QUESTION_BANK = [
  { tag: 'BEHAVIORAL', text: 'Tell me about a project that didn\'t go as planned. What did you do?' },
  { tag: 'TECHNICAL', text: 'Walk me through how you\'d design a rate limiter for a public API.' },
  { tag: 'SITUATIONAL', text: 'Your tech lead pushes a risky change at 5pm Friday. How do you respond?' },
  { tag: 'RESUME', text: 'You shipped a 3× perf win at Acme — what was the key insight?' },
  { tag: 'BEHAVIORAL', text: 'Describe a time you disagreed with a senior decision. What happened?' },
];

const QuestionGenDemo = () => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting && revealed === 0) setRevealed(1);
      }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [revealed]);

  useEffect(() => {
    if (revealed === 0 || revealed >= QUESTION_BANK.length) return;
    const t = setTimeout(() => setRevealed((r) => r + 1), 600);
    return () => clearTimeout(t);
  }, [revealed]);

  return (
    <section className="mil-section" ref={ref}>
      <div className="mil-section-grid">
        <div className="mil-section-text">
          <span className="mil-eyebrow">15 PERSONALIZED QUESTIONS</span>
          <h2 className="mil-h2">
            A full battery of <span className="mil-grad-text">role-tuned questions</span>, generated in seconds.
          </h2>
          <p className="mil-lead">
            Behavioral, technical, situational, and resume-based — covering every angle a real
            interviewer might take. Each one custom-generated for your role, level, and the JD
            you paste in.
          </p>
          <ul className="mil-bullet-list">
            <li><span className="mil-dot" />Tailored to seniority (entry / mid / senior / exec)</li>
            <li><span className="mil-dot" />Pulls from your resume so questions feel real</li>
            <li><span className="mil-dot" />Mix of question types — no two tests alike</li>
          </ul>
        </div>

        <div className="mil-questions-stack">
          {QUESTION_BANK.map((q, i) => (
            <div
              key={i}
              className={`mil-q-card ${i < revealed ? 'lit' : ''}`}
              style={{ transitionDelay: `${i * 50}ms`, '--depth': i }}
            >
              <span className="mil-q-tag">{q.tag}</span>
              <p>{q.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Model answer typewriter demo ---------- */
const ANSWER_FULL = 'When our checkout p95 jumped 3× during peak traffic, I led the war-room. Within 14 minutes we sharded the cart-summary cache and cut latency 38% — saving an estimated $42K in lost revenue and unblocking three downstream teams.';

const ModelAnswerDemo = () => {
  const ref = useRef(null);
  const [typed, setTyped] = useState('');
  const [phase, setPhase] = useState('idle');

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting && phase === 'idle') setPhase('typing');
      }),
      { threshold: 0.45 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [phase]);

  useEffect(() => {
    if (phase !== 'typing') return;
    let i = 0;
    const id = setInterval(() => {
      i += 1;
      setTyped(ANSWER_FULL.slice(0, i));
      if (i >= ANSWER_FULL.length) {
        clearInterval(id);
        setPhase('done');
      }
    }, 18);
    return () => clearInterval(id);
  }, [phase]);

  return (
    <section className="mil-section" ref={ref}>
      <div className="mil-section-grid mil-flip">
        <div className="mil-answer-card">
          <div className="mil-answer-header">
            <span className="mil-answer-label">MODEL ANSWER · STAR-formatted</span>
          </div>
          <p className="mil-answer-text">
            {typed}
            {phase === 'typing' && <span className="mil-caret">|</span>}
          </p>
          {phase === 'done' && (
            <div className="mil-answer-tags">
              <span>S — Situation</span>
              <span>T — Task</span>
              <span>A — Action</span>
              <span>R — Result</span>
            </div>
          )}
        </div>

        <div className="mil-section-text">
          <span className="mil-eyebrow">FULL MODEL ANSWERS</span>
          <h2 className="mil-h2">
            Every question comes with a <span className="mil-grad-text">first-person answer</span>.
          </h2>
          <p className="mil-lead">
            No vague prompts. Every question gives you a complete, recruiter-ready answer
            written for your role and experience level — STAR-structured, with quantified
            impact you can adapt to your real story.
          </p>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Pick your role', body: 'Job role, level, optional company, paste the JD or upload it.' },
  { title: 'Drop your resume', body: 'PDF / DOCX or "Use my profile" — we tailor every question to you.' },
  { title: 'Get 15 questions', body: 'Behavioral, technical, situational, resume-based — generated in seconds.' },
  { title: 'Read & record', body: 'Each question shows a model answer. You read it aloud and capture the take.' },
  { title: 'AI evaluates', body: 'Clarity, structure, confidence, pace, and content relevance — graded per answer.' },
  { title: 'Get your readiness score', body: 'Overall score + per-question feedback + the gaps to close before the real one.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="mil-section" ref={ref}>
      <div className="mil-section-head">
        <span className="mil-eyebrow">HOW IT WORKS</span>
        <h2 className="mil-h2">
          Six steps. <span className="mil-grad-text">Real-interview muscle memory.</span>
        </h2>
        <p className="mil-lead mil-center">
          A complete dry-run — questions, model answers, recording, and AI scoring — in 30 minutes.
        </p>
      </div>

      <div className="mil-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`mil-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="mil-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`mil-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 15, suffix: '', label: 'Questions per test' },
  { val: 5, suffix: 'min', label: 'To full readiness score' },
  { val: 87, suffix: '%', label: 'Users who pass next interview' },
  { val: 100, suffix: 'K+', label: 'Mock sessions run' },
];

const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1400;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};
const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="mil-stat">
      <div className="mil-stat-num">{v}{stat.suffix}</div>
      <div className="mil-stat-lbl">{stat.label}</div>
    </div>
  );
};
const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) setStart(true); });
    }, { threshold: 0.3 });
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="mil-stats-band" ref={ref}>
      <div className="mil-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'How are the questions generated?', a: 'Our model reads your role, seniority, the JD, and your resume — then composes 15 questions across behavioral, technical, situational, and resume-anchored categories. No two tests are identical.' },
  { q: 'What does the model answer give me?', a: 'A complete, first-person, STAR-structured answer for every question — written for your role at your level. You read it aloud, internalize the structure, and adapt the framework to your real stories.' },
  { q: 'How is my answer scored?', a: 'We evaluate clarity, structure (STAR adherence), confidence in delivery, pace, and content relevance to the question. Each gets a per-answer score, plus an overall readiness number.' },
  { q: 'Do I have to record?', a: 'Recording is optional but recommended — it\'s how the AI evaluates your delivery. You can always do silent practice runs and skip the scoring.' },
  { q: 'Is my recording stored?', a: 'Audio is processed in-stream for the evaluation and not retained after the test ends unless you explicitly save the session.' },
  { q: 'How long does a full test take?', a: 'Typical sessions run 25–35 minutes for all 15 questions. You can pause and resume.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="mil-section">
      <div className="mil-section-head">
        <span className="mil-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="mil-h2">Everything you wanted to ask</h2>
      </div>
      <div className="mil-faq-list">
        {FAQS.map((f, i) => (
          <button
            key={f.q}
            type="button"
            className={`mil-faq-item ${open === i ? 'open' : ''}`}
            onClick={() => setOpen(open === i ? -1 : i)}
          >
            <div className="mil-faq-q">
              <span>{f.q}</span>
              <span className="mil-faq-chev">+</span>
            </div>
            <div className="mil-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const MockInterviewLanding = () => {
  const navigate = useNavigate();
  const studioRef = useRef(null);
  const goStudio = useCallback(() => {
    if (studioRef.current) {
      studioRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const { user } = useUser();
  const setShowAuthModal = useCallback((show) => {
    if (show) navigate('/?openLogin=1');
  }, [navigate]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="mil-page">
      <Seo
        title="AI Mock Interview Practice with Instant Feedback"
        description="Practice realistic mock interviews with an AI interviewer and get instant, actionable feedback to improve."
        path="/mockInterview"
      />
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <section className="mil-hero" ref={heroRef}>
        <StageCanvas />
        <div className="mil-hero-bg" />
        <img src={HERO_IMG} alt="" className="mil-hero-img" loading="eager" />

        <div className="mil-hero-content">
          <span className="mil-hero-pill">
            <span className="mil-pill-dot" /> 15 questions · model answers · AI-graded
          </span>
          <h1 className="mil-h1">
            The dress rehearsal <span className="mil-grad-text">for your real interview</span>.
          </h1>
          <p className="mil-hero-sub">
            Fifteen role-tuned questions, full model answers, voice-recorded responses graded by
            AI on clarity, structure, confidence, and pace. Walk in with the muscle memory of
            someone who's already done it.
          </p>

          <div className="mil-hero-cta">
            <button className="mil-btn mil-btn-primary" onClick={goStudio}>
              <span>Start mock interview</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="mil-btn mil-btn-ghost" onClick={() => navigate('/InterviewAssistant')}>
              Get the live assistant
            </button>
          </div>

          <div className="mil-hero-checks">
            <span>✓ 15 questions per test</span>
            <span>✓ Full model answers</span>
            <span>✓ AI-scored delivery</span>
          </div>
        </div>

        {/* Floating glass score card in hero */}
        <div className="mil-floater" aria-hidden="true">
          <div className="mil-floater-card">
            <div className="mil-floater-head">
              <span>READINESS SCORE</span>
            </div>
            <div className="mil-floater-num">87<span>/100</span></div>
            <div className="mil-floater-bar">
              <div className="mil-floater-bar-fill" />
            </div>
            <div className="mil-floater-meta">
              <span>Clarity 9.1</span>
              <span>Structure 8.7</span>
              <span>Pace 8.4</span>
            </div>
          </div>
          <div className="mil-floater-glow" />
        </div>
      </section>

      <section className="mil-studio" ref={studioRef} id="studio">
        <div className="mil-studio-head">
          <span className="mil-eyebrow">MOCK INTERVIEW STUDIO</span>
          <h2 className="mil-h2">
            Run your full mock — <span className="mil-grad-text">live, in this page</span>.
          </h2>
          <p className="mil-lead mil-center">
            Job info, resume, 15 generated questions with model answers, recording, AI evaluation —
            the entire flow runs right here.
          </p>
        </div>
        <MockInterviewPage embedded />
      </section>

      <QuestionGenDemo />
      <ModelAnswerDemo />

      <section className="mil-section">
        <div className="mil-section-grid">
          <div className="mil-section-text">
            <span className="mil-eyebrow">AI EVALUATION</span>
            <h2 className="mil-h2">
              A coach's read on every answer — <span className="mil-grad-text">in seconds</span>.
            </h2>
            <p className="mil-lead">
              Each answer is graded across five axes: clarity, structure, confidence, pace, and
              content relevance. You see exactly which questions tripped you up and why — no
              guessing what to fix.
            </p>
            <ul className="mil-bullet-list">
              <li><span className="mil-dot" />Per-answer breakdown with strengths & gaps</li>
              <li><span className="mil-dot" />STAR-adherence detection</li>
              <li><span className="mil-dot" />Pace + filler-word counter</li>
            </ul>
          </div>
          <div className="mil-img-frame">
            <img src={SCORE_IMG} alt="AI evaluation radar chart" />
            <div className="mil-img-frame-glow" />
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="mil-section">
        <div className="mil-section-grid mil-flip">
          <div className="mil-img-frame">
            <img src={QUESTIONS_IMG} alt="AI question generator" />
            <div className="mil-img-frame-glow" />
          </div>
          <div className="mil-section-text">
            <span className="mil-eyebrow">RESUME-AWARE QUESTIONS</span>
            <h2 className="mil-h2">
              Questions that <span className="mil-grad-text">read like a recruiter</span> just opened your CV.
            </h2>
            <p className="mil-lead">
              "You shipped X at Y — what was the inflection point?" — questions anchored to the
              actual roles, projects, and metrics in your resume. Not generic prompts.
            </p>
          </div>
        </div>
      </section>

      <section className="mil-section">
        <div className="mil-section-grid">
          <div className="mil-section-text">
            <span className="mil-eyebrow">PROVEN STRUCTURE</span>
            <h2 className="mil-h2">
              Every model answer is <span className="mil-grad-text">STAR-structured</span>.
            </h2>
            <p className="mil-lead">
              Situation. Task. Action. Result. The structure recruiters scan for in 15 seconds —
              built into every model answer so you internalize the shape, then bring your own story.
            </p>
          </div>
          <div className="mil-img-frame">
            <img src={ANSWER_IMG} alt="Model answer panel" />
            <div className="mil-img-frame-glow" />
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="mil-cta-final">
        <div className="mil-cta-card">
          <div className="mil-cta-glow" />
          <h2 className="mil-h2">
            The best interviewers <span className="mil-grad-text">rehearse</span>.
          </h2>
          <p>Run the dress rehearsal. Walk in ready.</p>
          <div className="mil-hero-cta center">
            <button className="mil-btn mil-btn-primary" onClick={goStudio}>
              <span>Start mock interview</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="mil-btn mil-btn-ghost" onClick={() => navigate('/InterviewAssistant')}>Get live assistant</button>
          </div>
        </div>
      </section>

      <FaqSchema faqs={FAQS} />
      <Footer />
    </div>
  );
};

export default MockInterviewLanding;
